import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardCheck, CheckCircle2, MessageSquareWarning, FileText } from "lucide-react";
import { FormValues } from "@/components/FormFields";

const STATUS = {
  pending: { label: "Please review & confirm", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confirmed", cls: "bg-green-100 text-green-700" },
  changes_requested: { label: "Changes requested", cls: "bg-red-100 text-red-700" },
};

const SharedForm = ({ accessToken, form, onDone }) => {
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [mode, setMode] = useState(null); // confirm | changes
  const [busy, setBusy] = useState(false);
  const conf = form.confirmation || { status: "pending" };
  const s = STATUS[conf.status] || STATUS.pending;

  const submit = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/public/${accessToken}/forms/${form.submission_id}/confirm`, { action, name, comment });
      toast.success(r.data.message);
      setMode(null); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid={`portal-form-${form.submission_id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> {form.form_name} <span className="text-xs font-normal text-slate-400">Rev {form.revision}</span></CardTitle>
          <Badge className={s.cls} data-testid="portal-form-status">{s.label}</Badge>
        </div>
        {conf.status === "confirmed" && <p className="text-xs text-green-700">Confirmed by {conf.name} on {new Date(conf.at).toLocaleDateString()}</p>}
        {conf.status === "changes_requested" && <p className="text-xs text-red-700">You asked for: "{conf.comment}" — we'll update this and ask you to confirm again.</p>}
        {conf.previous?.status === "confirmed" && conf.status === "pending" && <p className="text-xs text-amber-700">This form was updated since you last confirmed — please review the new version.</p>}
      </CardHeader>
      <CardContent className="space-y-3">
        <FormValues fields={form.fields} data={form.data} />
        {conf.status !== "confirmed" && (
          <div className="border-t pt-3 space-y-2">
            {!mode && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setMode("confirm")} data-testid="portal-form-confirm-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> These selections are correct</Button>
                <Button variant="outline" onClick={() => setMode("changes")} data-testid="portal-form-changes-btn"><MessageSquareWarning className="w-4 h-4 mr-2" /> Something needs to change</Button>
              </div>
            )}
            {mode === "confirm" && (
              <div className="space-y-2 bg-green-50 rounded-lg p-3">
                <p className="text-sm text-slate-700">By typing your name you confirm these selections are correct and approve them for production.</p>
                <Input placeholder="Type your full name" value={name} onChange={e => setName(e.target.value)} data-testid="portal-confirm-name" />
                <div className="flex gap-2"><Button onClick={() => submit("confirm")} disabled={busy || !name.trim()} data-testid="portal-confirm-submit">Confirm</Button><Button variant="ghost" onClick={() => setMode(null)}>Back</Button></div>
              </div>
            )}
            {mode === "changes" && (
              <div className="space-y-2 bg-amber-50 rounded-lg p-3">
                <Textarea rows={3} placeholder="Tell us what should be different (e.g. door colour should be Navy, add 2 more drawer boxes)…" value={comment} onChange={e => setComment(e.target.value)} data-testid="portal-changes-comment" />
                <Input placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)} />
                <div className="flex gap-2"><Button onClick={() => submit("request_changes")} disabled={busy || !comment.trim()} data-testid="portal-changes-submit">Send request</Button><Button variant="ghost" onClick={() => setMode(null)}>Back</Button></div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Customer-facing "Selections to confirm" tab.
export const PortalFormsTab = ({ accessToken, forms = [], onChanged }) => (
  <div className="space-y-4 animate-fade-in" data-testid="portal-forms-tab">
    <div>
      <h2 className="text-lg font-semibold text-slate-900">Your selections</h2>
      <p className="text-sm text-slate-500">Please review the finish and design sheets below and confirm they're correct before we start production.</p>
    </div>
    {forms.length === 0 ? (
      <Card><CardContent className="py-10 text-center text-slate-500"><ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />Nothing to review right now.</CardContent></Card>
    ) : forms.map(f => <SharedForm key={f.submission_id} accessToken={accessToken} form={f} onDone={onChanged} />)}
  </div>
);
